import { ReactElement, useState, useEffect } from "react";
import { Button, Col, Drawer, Form, Input, Row, Select, Upload, message, Checkbox,Switch } from "antd";
import slugify from "react-slugify";
import { InboxOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import firebase from "firebase/app";
import { DrawerFooter } from "../../components/GlobalStyles";
import { SET_LOADING_STATE, RESET_LOADING_STATE, CREATE_PRODUCT_REQUEST,FETCH_ALL_PRODUCTS_REQUEST } from "../../redux/constants";
import { getUserState, getAuthState } from "../../redux/selectors";
import { countries, clubStatus, currencies } from "../../constants";

const { useForm } = Form;
const { Option } = Select;
const { TextArea } = Input;

type Props = {
  visible: boolean;
  onClose: () => void;
};

function beforeUpload(file: any) {
  const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
  if (!isJpgOrPng) {
    message.error("You can only upload JPG/PNG file!");
  }
  return false;
}

export function CreateProductDrawer({ visible, onClose }: Props): ReactElement {
    const dispatch = useDispatch();
    const { users } = useSelector(getUserState);
    const [form] = useForm();
    const [loading, setLoading] = useState<boolean>(false);
    const [condition, setCondition] = useState([
        {
            "name":"Excellent",
            "value" : 1,
        },
        {
            "name":"Good",
            "value" : 2,
        },
        {
            "name":"Heavily used",
            "value" : 3,
        },
        {
            "name":"New",
            "value" : 4,
        },
    ])
    const [category, setCategory] = useState<any[]>([]);
    const [productID,setProductID] = useState(new Date().valueOf().toString());
    const [discoutnAvailable, setDiscountAvailable] = useState<number>(0);
    const { t } = useTranslation();
    const storage = firebase.storage();
    const shippingOption = [ 'Yes', 'No' ];
    const { currentUser } = useSelector(getAuthState);
    const [subCategory, setSubCategory] = useState<any[]>([]);
    const [currency, setCurrency] = useState<string>("AED");

    const getCategoryItems = async () => {
        const categoryList = await firebase.firestore().collection("category").where('parent', "==", 0).get();
        if(categoryList.size !== 0)
        {
            const list = categoryList.docs;
            const catList = list.map(cat => cat.data());
            setCategory(catList);
        }
    }

    useEffect(() => {
        getCategoryItems();
        const newDate = new Date().valueOf();
        setProductID(newDate.toString());
    }, []);
    
    const onChangePriceCurreny = (value: string) => {
        setCurrency(value);
    };
    
    const getClubId = async () => {
        const lastClubsSnapshot = await firebase.firestore().collection("clubs").orderBy('clubId', "desc").limit(1).get();
        if(lastClubsSnapshot.size === 0) {
            return "001";
        }
        const { clubId } = lastClubsSnapshot.docs[0].data();
        const newClubId = parseInt(clubId, 10) + 1;
        if(newClubId < 10 ) return `00${newClubId}`;
        if(newClubId > 9 && newClubId < 100) return `0${newClubId}`;
        return `${newClubId}`;
    }

    const onFinish = async (values: any) => {
        dispatch({ type: SET_LOADING_STATE});
        onClose();
        const formValue = {
            ...values,
            role : currentUser?.role,
            id : currentUser?.id,
        };
        if (values.photo) {
            const ref = `products/${values.category}/${values.product_id}`;
            await storage.ref(ref).put(values.photo.file);
            const productImageUrl = await storage.ref(ref).getDownloadURL();
            formValue.photo = productImageUrl;
        } else {
            formValue.photo = "";
        }
        Object.keys(formValue).forEach((key) => {
            if (formValue[key] === undefined) formValue[key] = "";
        });

        dispatch({ type: CREATE_PRODUCT_REQUEST, payload: formValue });
        dispatch({ type: FETCH_ALL_PRODUCTS_REQUEST, payload: { currentUser } });
        dispatch({ type: RESET_LOADING_STATE});
        form.resetFields();
    };

    const onFinishFailed = (errorInfo: any) => {
        console.log("Failed:", errorInfo);
    };

    const onChangeDiscount = (value : any) => {
        setDiscountAvailable(value);
    }

    const changeParentCategory = async (values:any) => {
        if(values === 0)
        {
            return;
        }
        const categoryList = await firebase.firestore().collection("category").where('parent', "==", values).get();
        if(categoryList.size !== 0)
        {
            const list = categoryList.docs;
            const catList = list.map(cat => cat.data());
            setSubCategory(catList);
        }
    }
    return (
        <Drawer
            title={t("market.create_market")}
            width={420}
            onClose={onClose}
            visible={visible}
            bodyStyle={{ paddingBottom: 80 }}
        >
            <Form 
                form={form} 
                layout="vertical" 
                onFinish={onFinish} 
                onFinishFailed={onFinishFailed}
                initialValues={{
                    name : "",
                    manager: [],
                    shipping: 1,
                    visibility: 1,
                    product_id : productID,
                    condition : 1,
                    currency:"AED"
                }}
            >
                {/* Club name and manager */}
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item 
                            name="category" 
                            label={t("market.category")} 
                            rules={[
                                {
                                required: true,
                                message: t("market.category"),
                                },
                            ]}
                        >
                            <Select 
                                onChange = {changeParentCategory}
                                placeholder={t("market.category")}>
                                { category.map(({name, catID}) => (
                                <Option key={catID} value={catID}>{name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    {subCategory.length !== 0 && 
                    <Col span={24}>
                        <Form.Item 
                            name="subcategory" 
                            label={t("market.subCategory")} 
                        >
                            <Select 
                                placeholder={t("market.subCategory")}>
                                { subCategory.map(({name, catID}) => (
                                <Option key={name} value={catID}>{name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    }
                    <Col span={24}>
                        <Form.Item
                        name="name"
                        label={t("market.product_name")}
                        rules={[
                            {
                            required: true,
                            message: t("market.product_name_placeholder"),
                            },
                        ]}
                        >
                        <Input placeholder={t("market.product_name_placeholder")} />
                        </Form.Item>
                    </Col>
                    {
                        currentUser && currentUser.role === "manager" ? 
                        <Col span={24}>
                            <Form.Item
                                name="product_id"
                                label={t("market.product_id")}
                            >
                                <Input
                                    disabled
                                    placeholder={t("market.product_id")} 
                                />
                            </Form.Item>
                        </Col>
                        :
                        <Col span={12}>
                            <Form.Item
                                name="product_id"
                                label={t("market.product_id")}
                            >
                                <Input
                                    disabled
                                    placeholder={t("market.product_id")} 
                                />
                            </Form.Item>
                        </Col>
                    }
                    {
                        currentUser && currentUser.role === "manager" &&
                        <Col span={12}>
                            <Form.Item
                                name="discount"
                                label={t("market.product_discount")}
                            >
                                <div style={{display:"flex"}}>
                                    <Input.Group compact>
                                        <Select 
                                            defaultValue="No" 
                                            onChange={onChangeDiscount}
                                            style={{ width: '40%' }}
                                        >
                                            <Option value={1}>Yes</Option>
                                            <Option value={0}>No</Option>
                                        </Select>
                                        { discoutnAvailable === 1 ? 
                                            <Input
                                                style={{ width: '60%' }}
                                                type="number"
                                                placeholder={t("market.product_discount_placeholder")} 
                                            />
                                        : 
                                            <Input
                                                style={{ width: '60%' }}
                                                type="number"
                                                disabled
                                                placeholder={t("market.product_discount_placeholder")} 
                                            />
                                        }
                                        
                                    </Input.Group>
                                    
                                </div>
                            </Form.Item>
                        </Col>
                    }
                    <Col span={12}>
                        <Form.Item name="currency" label={t("market.product_currency")} >
                            <Select placeholder={t("market.product_currency_placeholder")}>
                                {currencies.map( c => <Option value={c.value}>{c.label}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="cost"
                            label={t("market.product_price")}
                            rules={[
                                {
                                required: true,
                                message: t("market.product_price_placeholder"),
                                },
                            ]}
                        >
                            <Input 
                                type="number"
                                required
                                placeholder={t("market.product_price_placeholder")} 
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                        name="quantity"
                        label={t("market.product_quantity")}
                        rules={[
                            {
                            required: true,
                            message: t("market.product_quantity_placeholder"),
                            },
                        ]}
                        >
                            <Input 
                                type="number"
                                required
                                placeholder={t("market.product_quantity_placeholder")} 
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="condition" label={t("market.product_condition")} >
                            <Select placeholder={t("market.product_condition_placeholder")}>
                                { condition.map(({name, value}) => (
                                <Option key={value} value={value}>{name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="shipping" label={t("market.product_shipping")} >
                            <Select placeholder={t("market.product_shipping")}>
                                <Option value={1}>Yes</Option>
                                <Option value={0}>No</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="visibility" label={t("market.product_visible")} >
                            <Select placeholder={t("market.product_visible")}>
                                <Option value={1}>Yes</Option>
                                <Option value={0}>No</Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item
                        name="tags"
                        label={t("market.product_tags")}
                        rules={[
                            {
                                required: true,
                                message: t("market.product_tags_placeholder"),
                            },
                        ]}
                        >
                            {/* <Input 
                                required
                                placeholder={t("market.product_tags_placeholder")} 
                            /> */}
                            <Select mode="tags" style={{ width: '100%' }} showArrow={false} placeholder="Tags Mode" />
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item name="description" label={t("market.product_description")} >
                            <TextArea 
                                placeholder={t("market.product_description_placeholder")}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                {/* Photo */}
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item 
                            name="photo" 
                            label="Photo" 
                            rules={[
                                {
                                required: true,
                                message: t("market.photo_placeholder"),
                                },
                            ]}
                        >
                        <Upload.Dragger
                            name="files"
                            beforeUpload={beforeUpload}
                            showUploadList
                        >
                            <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">
                            Click or drag file to this area to upload
                            </p>
                            <p className="ant-upload-hint"> Support for a single upload.</p>
                        </Upload.Dragger>
                        </Form.Item>
                    </Col>
                </Row>
                <DrawerFooter>
                    <Button onClick={onClose} style={{ marginRight: 8 }}>
                        {t("common.cancel")}
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        {t("common.submit")}
                    </Button>
                </DrawerFooter>
            </Form>
        </Drawer>
    );
}
